import {Button, Form, Input, message, Modal, Select, Spin} from "antd";
import buttonStyles from "../../components/common/styled/Buttons.module.scss";
import {MinusCircleOutlined, PlusOutlined} from "@ant-design/icons";
import HorizontalScrollImageCheckbox from "../../components/inputs/HorizontalScrollImageCheckbox";
import ApplicationMessageBodyParser from "../../components/inputs/ApplicationMessageBodyParser";
import React, {useCallback, useEffect, useState} from "react";
import {deleteView} from "../../components/Tableau/TableauViz";
import {CustomView} from "tableau-js-api/workbook";
import {getEmailTemplate, postEmailTemplate} from "../../actions/storyboardApi";
import {SheetInfo} from "tableau-js-api/sheet";
import {unstable_batchedUpdates} from "react-dom";
import {Viz} from "tableau-js-api/viz";
import {generateRandomHexString} from "../../util/StringUtils";

const GenericSendEmailAttachmentModel = ({viz, isOpen, onCancel}: {viz: Viz, isOpen: boolean, onCancel() : void,}) => {

    const [form] = Form.useForm();
    const [applicationMessageTemplate, setApplicationMessageTemplate] = useState<string | undefined>(undefined);
    const [applicationMessageValues, setApplicationMessageValues] = useState<{ [key: string]: string }>({})
    const [publishedSheets, setPublishedSheets] = useState<SheetInfo[]>([]);
    const [emails, setEmails] = useState([""]);
    const [attachmentOption, setAttachmentOption] = useState< "all" | "choice">("all");
    const [selectedSheets, setSelectedSheets] = useState<number[]>([]);
    const [applicationMessagesBodyReady, setApplicationMessagesBodyReady] = useState(false);
    const [isSending, setSending] = useState(false);

    const FORM_NAME = "generateEmailForm"

    useEffect(() => {
        if (applicationMessageTemplate === undefined) {
            getEmailTemplate(viz.getWorkbook().getName()).then(response => {
                setApplicationMessageTemplate(response.template);
            })
        }
    }, [applicationMessageTemplate, viz])

    const resetSelectedSheets = useCallback((sheets: SheetInfo[]) => {
        sheets.forEach(sheet => {
            const isDisclaimer = isSheetDisclaimer(sheet);
            if (isDisclaimer) {
                setSelectedSheets([sheet.getIndex()])
            }
        })
    }, [])

    useEffect(() => {
        const sheets = viz.getWorkbook()
            .getPublishedSheetsInfo() || [];

        setPublishedSheets(sheets);

        resetSelectedSheets(sheets)
    }, [viz, resetSelectedSheets])


    const handleSend = async (formFields:{emails: string[]}) => {
        setSending(true);

        const emailRecipients = emails;
        let sheets : number[] = [];
        if (attachmentOption === "all") {
            if (viz.getWorkbook()) {
                const all = viz.getWorkbook().getPublishedSheetsInfo().length;
                sheets = Array.from({length: all}, (v, i) => i); // [0,1,2,3]
            }
        } else {
            sheets = selectedSheets;
        }

        const userInput = applicationMessageValues;

        const customViews : CustomView[] = [];
        const returnSheet = viz.getWorkbook().getActiveSheet();
        for(const sheet of sheets) {
            await viz.getWorkbook().activateSheetAsync(sheet);
            const customView = await viz.getWorkbook().rememberCustomViewAsync(generateRandomHexString(12));
            customViews.push(customView)
        }
        const workbookName = viz.getWorkbook().getName()
        await viz.getWorkbook().activateSheetAsync(returnSheet.getIndex());

        const viewURLs = customViews.map(customView => customView.getUrl()+".pdf")

        postEmailTemplate({
            workbookName,
            emailRecipients,
            userInput,
            viewURLs
        })
            .then(() => {
                message.success(`Email sent successfully!`)
            })
            .catch(() => {
                message.error("There was an error whilst sending the email!")
            })
            .finally(async() => {
                for(const customView of customViews) {
                    await deleteView(customView.getName())
                }
                unstable_batchedUpdates(() => {
                    setSending(false);
                    setApplicationMessageTemplate(undefined);
                    handleCancel()
                })

            })
    };

    const isSheetDisclaimer = (sheet: SheetInfo): boolean => {
        return sheet.getName().toLowerCase() === "disclaimer"
    }

    const handleCancel = () => {
        form.resetFields()
        setEmails([""])
        setAttachmentOption("all");
        resetSelectedSheets(publishedSheets);
        setApplicationMessageValues({});
        setApplicationMessagesBodyReady(false);
        onCancel();
    }

    const emailsReady = emails.filter(email => email.length >0).length === emails.length
    const disableSend = isSending || !applicationMessagesBodyReady || !emailsReady;

    return <Modal title="Send Storyboard as Email Attachment"
                  open={isOpen}
                  onCancel={handleCancel}
                  centered
                  width={"50%"}
                  footer={[
                      <Button className={buttonStyles.greenPrimary}
                              key="send-email"
                              form={FORM_NAME} htmlType="submit"
                              disabled={disableSend}
                      >
                          Send
                      </Button>
                  ]}>
        <Form form={form}
              name={FORM_NAME}
              autoComplete="off"
              onFinish={handleSend}
        >
            {isSending ? <div style={{display: "flex", justifyContent: "center"}}>
                <Spin tip="Sending..." spinning={isSending}/>
            </div> : <><p>Recipients:</p>
                {emails.map((email, i) =>
                    <div style={{marginBottom: "16px"}} key={i}>
                        <Input
                            type={"email"}
                            value={emails[i]}
                            placeholder="Insert email address"
                            style={{width: "90%", marginRight: "8px"}}
                            onChange={(e) => {
                                const value = e.currentTarget.value;
                                const emailsClone = JSON.parse(JSON.stringify(emails));
                                emailsClone[i] = value;
                                setEmails(emailsClone);
                            }}
                        />
                        {emails.length < 2 ? null : <MinusCircleOutlined
                            className="dynamic-delete-button"
                            onClick={() => {
                                const emailsClone = JSON.parse(JSON.stringify(emails));
                                emailsClone.splice(i, 1);
                                setEmails(emailsClone);
                            }}
                        />}
                    </div>
                )}
                {emails.length < 20 && <Button
                    type="dashed"
                    onClick={() => {
                        const emailsClone = JSON.parse(JSON.stringify(emails));
                        emailsClone[emailsClone.length] = "";
                        setEmails(emailsClone);
                    }}
                    style={{width: '100%'}}
                    icon={<PlusOutlined/>}
                >
                    Add Email
                </Button>}

                <hr/>

                <p>Attachment:</p>
                <Select
                    value={attachmentOption}
                    onChange={setAttachmentOption}
                    options={[
                        {value: 'all', label: 'All sheets from this dashboard'},
                        {value: 'choice', label: 'Selected sheets from this dashboard'},
                    ]}
                />

                {attachmentOption === "choice" && <HorizontalScrollImageCheckbox imageCheckboxes={
                    publishedSheets
                        .map(sheet => {
                            const isDisclaimer = isSheetDisclaimer(sheet)
                            return {
                                label: sheet.getName(),
                                checked: isDisclaimer || selectedSheets.includes(sheet.getIndex()),
                                onChange: (event => {
                                    if (isDisclaimer) {
                                        return;
                                    }
                                    let sheetClone: number[] = JSON.parse(JSON.stringify(selectedSheets));
                                    if (event.target.checked) {
                                        //     add
                                        sheetClone.push(sheet.getIndex());
                                    } else {
                                        //    remove
                                        sheetClone = sheetClone.filter(sheetIndex => sheetIndex !== sheet.getIndex());
                                    }
                                    sheetClone = sheetClone.sort();
                                    setSelectedSheets(sheetClone);
                                })
                            }
                        })}
                />
                }

                <hr/>


                <p>Message Body</p>
                <div style={{padding: "8px", paddingBottom: "24px", border: "1px solid #d9d9d9"}}>
                    {applicationMessageTemplate === undefined ?
                        <Spin tip="Loading..."/> :
                        <ApplicationMessageBodyParser
                            template={applicationMessageTemplate}
                            values={applicationMessageValues}
                            onReadyChange={ready => setApplicationMessagesBodyReady(ready)}
                            onKeyValueChange={(k, v) => {
                                const clone = JSON.parse(JSON.stringify((applicationMessageValues)));
                                clone[k] = v;
                                setApplicationMessageValues(clone);
                            }}
                        />
                    }
                </div>
            </>}
        </Form>
    </Modal>
}

export default GenericSendEmailAttachmentModel;